// Recipe interface — the contract every vertical must satisfy.
//
// The engine ships zero recipes; concrete recipes live in
// @bitacorapro/recipes. The engine only defines the SHAPE so it can validate
// reports/findings/parties/signatures against whatever recipe the
// organization is using.
//
// What lives here vs. in the recipe:
//   - Engine: status enums, evidence types, the six-entity model.
//   - Recipe: labels, report types, default areas, finding categories,
//     severities, party roles, signature roles, AI prompt profile id, PDF
//     template id.
//
// `aiPromptProfile` and `pdfTemplate` are string IDs only — the engine never
// loads prompts or templates. ai-svc and the PDF renderer resolve them.
import { slugSchema } from "@bitacorapro/shared";
import { z } from "zod";
import { ownerSurveyNpsKindSchema } from "./owner-surveys.js";
import { DERIVED_SEVERITIES } from "./taxonomy.js";
import { UNIT_PROFILE_PROTECTED_KEYS } from "./unit-profile.js";
// The base nouns (organization … evidence) name the six core entities so the
// engine and UI share a vocabulary per vertical. The remaining fields are
// derived UI strings the FE renders verbatim — keeping them in the recipe
// avoids hardcoding "Propiedad"/"Tienda" variants screen by screen, and lets
// us tune wording (gender, register, brand voice) per vertical without code
// changes. Add a new entry here whenever a screen would otherwise hardcode a
// string that depends on the active vertical.
//
// Exported so org-config.ts can derive `labelOverrides` via labelsSchema.partial()
// — the per-org overrides are strictly typed against the same set of keys, so
// a typo in `organizations.config.labelOverrides` is rejected at parse time.
export const labelsSchema = z.object({
    organization: z.string().min(1),
    assetGroup: z.string().min(1),
    asset: z.string().min(1),
    report: z.string().min(1),
    area: z.string().min(1),
    finding: z.string().min(1),
    evidence: z.string().min(1),
    // Plural of `asset` — used in section headers and empty states.
    assetPlural: z.string().min(1),
    // Plural of `area` — used in section headers ("Sectores capturados",
    // "Recintos capturados") and inline count copy. New in ADR-0020.
    areaPlural: z.string().min(1),
    // Plural of `finding` — used in count strings ("3 observaciones", "0
    // observaciones") in revision and asset-detail screens. New in ADR-0020.
    findingPlural: z.string().min(1),
    // Display noun for the supervisor role in dashboards and read-model
    // surfaces (e.g. "Supervisor: <name>" sublines, manager dashboard
    // "Equipo" section). Distinct from partyRoles[].label which is signed
    // into the acta. New in ADR-0020.
    supervisorNoun: z.string().min(1),
    // Full sentences / CTAs the FE shows verbatim. Stored as full strings (not
    // composed from `asset`) so verticals can choose article and register
    // freely (e.g. "Nueva tienda" vs "Nueva unidad").
    assetNew: z.string().min(1),
    assetCreateCta: z.string().min(1),
    assetSaveCta: z.string().min(1),
    assetPickerTitle: z.string().min(1),
    assetEmptyState: z.string().min(1),
    // Multi-line body copy for the empty state on /cartera when an org has
    // no assets. Renders below the title; the title is recipe-driven via
    // `assetEmptyState` (above). New in ADR-0020.
    assetEmptyStateBody: z.string().min(1),
    assetSearchPlaceholder: z.string().min(1),
    // Placeholder for the group-mode search bar on /cartera (postventa /
    // facility). Distinct from `assetSearchPlaceholder` which targets the
    // flat asset list. New in ADR-0020.
    assetGroupSearchPlaceholder: z.string().min(1),
    // Name of the vertical itself, used as a subtitle on the asset-creation
    // screen ("Arriendos", "Facility Management", "Postventa").
    verticalName: z.string().min(1),
    // Heading of the "owner-ish" party section in the asset-creation form
    // (B2C: Propietario; facility B2B: Administrador; postventa B2B: Comprador).
    assetOwnerSection: z.string().min(1),
    // Label of the one-line field rendered on `/cartera/:groupId/:assetId`'s
    // AssetInfoBlock (Sesión 4 Patch S4.6 — single field per vertical, not
    // four). Postventa: "Propietario"; facility: "Jefe de tienda"; arriendos
    // keeps "Propietario" for now.
    assetInfoField: z.string().min(1),
    // The KEY of the field inside `asset.attributes` that backs the
    // AssetInfoBlock — the FE reads `asset.attributes[assetInfoFieldKey]`.
    // Distinct from `assetInfoField` which is the visible label. Closes
    // the ADR-0019 violation at cartera/[id]/[assetId]/+page.svelte:156-163
    // (the slug branch is replaced by reading this key). New in ADR-0020.
    assetInfoFieldKey: z.string().min(1),
    // Plural of `assetGroup` — used as the cartera header / section titles.
    // Distinct from the singular `assetGroup` (which reads "Proyecto" /
    // "Zona" / "Cartera"). Section header on /cartera reads this verbatim.
    assetGroupPlural: z.string().min(1),
    // "reportada" relabeling (ADR-0098 postscript) — display copy for a
    // report that reached a terminal state WITHOUT a signature (facility's
    // unilateral attestation closure). Consulted only when
    // reportTerminalKind(report) returns "attested"; the ordinary "Firmada"
    // wording elsewhere in the app is unconditional literal text, not
    // recipe-driven, and stays exactly as-is for the "signed" kind. Every
    // recipe declares a value (required, matching this schema's
    // zero-optional-keys convention) even though only facility_management's
    // ever gets selected — residential_rental and broker_postventa never
    // produce an "attested" report, so their values are dead-but-declared,
    // the honest cost of not needing a per-vertical `?? "Firmada"` fallback
    // at every call site. NEVER derive the "signed" kind's wording from
    // these keys — that is the exact misstatement ADR-0098 rejected as
    // Option G, applied in the other direction (understating a genuinely
    // signed historical acta).
    reportAttestedWord: z.string().min(1),
    reportAttestedWordPlural: z.string().min(1),
    reportAttestedTitle: z.string().min(1),
    reportAttestedPublicIntegrityLine: z.string().min(1),
    reportAttestedPublicBannerTitle: z.string().min(1),
    reportAttestedPublicFooterFragment: z.string().min(1),
    reportAttestedPublicMeta: z.string().min(1),
    reportAttestedMutationBlockedMessage: z.string().min(1),
});
// Capture mode is per Report-type, not per recipe — a single recipe can mix
// (postventa: `acta_entrega` runs as a contextual modal while `acta_visita`
// runs full-screen). See Spec Arquitectural §2.3.
const captureModeSchema = z.enum(["fullscreen", "modal_contextual"]);
// Where a reportType's area catalog comes from, and whether it exists at
// all. "shared_pool" is today's behavior for every recipe shipped before
// this field existed: areas come from the recipe-root `defaultAreas` pool,
// open to ad-hoc additions via the FE's custom-entry path. Three more
// values exist for reportTypes that need something the shared pool can't
// express: "predefined_per_asset" — a closed catalog stored per asset
// (varies by the asset's real floor plan; no ad-hoc additions), fetched via
// GET /reports/:id/area-templates. "none" — no area concept at all;
// captures attach to a single internal sentinel area and findings carry
// their location in `finding.element` (free text) instead.
// "org_checklist_template" (ADR-0068) — the org-authored, brand-scoped
// `checklist_templates`/`inspection_areas` catalog, now split across one
// or more named templates per brand instead of a single flat pool;
// facility's acta_visita declares this explicitly (see
// packages/recipes/src/facility_management.ts). Defaulted so existing
// recipes need no edits — see ADR-0056.
const areaCatalogModeSchema = z.enum([
    "shared_pool",
    "predefined_per_asset",
    "none",
    "org_checklist_template",
]);
// Recipe-driven single-page form for a milestone reportType (ADR-0058
// — postventa's acta_entrega: "I. Datos generales", "II. Documentación
// entregada", "III. Recepción de artefactos", "IV. Lectura de
// medidores", "V. Conformidad del proceso"). The reference app
// (bitacora-postventa) hardcodes these five sections directly in the
// component; this recipe declares them as data instead, so the FE
// never branches on `recipe.slug` to know what the form looks like —
// same "primitive, not slug branch" posture as severities/
// findingCategories/assetStatuses. Optional on reportTypeSchema —
// only milestone reportTypes that need a structured form declare it;
// everything else omits it and the FE renders no form section at all.
//
// Four section kinds cover every shape the reference app uses:
//   "fields"    — plain text/email/tel/number inputs (Datos generales).
//   "checklist" — a closed item list, each answered from a shared
//                 option set (Documentación entregada's Sí/No/N/A,
//                 Recepción de artefactos' Conforme/No conforme/N/A,
//                 Conformidad del proceso's Sí/No). The option set is
//                 data on the section, not a fourth kind, so a new
//                 vocabulary needs no schema change.
//   "meters"    — paired (meter number, reading) rows per utility
//                 (Lectura de medidores).
//   "notes"     — a single free-text field (Observaciones).
const milestoneFormFieldSchema = z.object({
    key: slugSchema,
    label: z.string().min(1),
    // "rut" renders as a plain text input (browsers have no native RUT
    // type) but signals the FE to apply the shared formatRutLive
    // blur-time formatter — a declared input flavor, not a hardcoded
    // field-key check in product code, same posture as email/tel/number
    // already being more than a bare type= pass-through.
    inputType: z.enum(["text", "email", "tel", "number", "rut"]).default("text"),
});
const milestoneFormChecklistOptionSchema = z.object({
    key: z.string().min(1),
    label: z.string().min(1),
});
const milestoneFormChecklistItemSchema = z.object({
    key: slugSchema,
    label: z.string().min(1),
});
const milestoneFormMeterSchema = z.object({
    key: slugSchema,
    label: z.string().min(1),
});
const milestoneFormSectionSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("fields"),
        key: slugSchema,
        title: z.string().min(1),
        fields: z.array(milestoneFormFieldSchema).min(1),
    }),
    z.object({
        kind: z.literal("checklist"),
        key: slugSchema,
        title: z.string().min(1),
        options: z.array(milestoneFormChecklistOptionSchema).min(2),
        items: z.array(milestoneFormChecklistItemSchema).min(1),
    }),
    z.object({
        kind: z.literal("meters"),
        key: slugSchema,
        title: z.string().min(1),
        meters: z.array(milestoneFormMeterSchema).min(1),
    }),
    z.object({
        kind: z.literal("notes"),
        key: slugSchema,
        title: z.string().min(1),
    }),
]);
// Declares that signing a report of this reportType should project
// data onto the report's Asset attributes — e.g. postventa's Acta de
// Entrega confirming/capturing the property owner's contact data,
// which the property page reads from Asset attributes going forward.
// Recipe-declared so core-api's sign handler never hardcodes a
// reportType/slug check or a vertical's field-key vocabulary (same
// "primitive, not slug branch" posture as milestoneForm above). Only
// fills Asset attributes that are currently empty — never overwrites
// data the Asset already carries (a report-typed fallback is not more
// authoritative than deliberately-curated Asset data).
const assetAttributeSyncSchema = z.object({
    // Dot-separated path within report.adminData to the per-report
    // form-field object holding values that never reach `parties` (e.g.
    // postventa's correo/telefono, autosaved into
    // adminData.actaEntregaForm.datos_generales). Each segment is a
    // plain object-key lookup.
    adminDataPath: z.string().min(1),
    // milestoneForm field key -> Asset attribute key, resolved against
    // the object at adminDataPath.
    fieldToAttributeKey: z.record(z.string(), z.string()),
    // The signed party role carrying the name + id document for this
    // sync (e.g. postventa's "comprador"). Read from the already-
    // validated `parties` array, not from adminData.
    partyRole: z.string().min(1),
    partyNameAttributeKey: z.string().min(1),
    partyIdDocAttributeKey: z.string().min(1),
});
// The observation-status actor lanes (ADR-0105 / ADR-0129). Documented
// next to observationStatusSchema below; declared here because
// reportType.creatableByActors reads the same vocabulary and a `const`
// referenced before its declaration is a TDZ error at module load.
const observationStatusActorSchema = z.enum([
    "session_user",
    "assigning_party",
    "share_link",
    "asset_party",
]);
const reportTypeSchema = z.object({
    key: slugSchema,
    label: z.string().min(1),
    captureMode: captureModeSchema,
    // True when this reportType is an administrative milestone — at most
    // one per (asset, reportType). Drives `reports.is_milestone` and the
    // asset detail's "milestones" map. Default false keeps the recurring
    // reportTypes (visitas, checks) untouched. See Sesión 4 §5.undecies +
    // the partial unique index `reports_milestone_unique`.
    milestone: z.boolean().default(false),
    // Optional descriptive sub-line rendered by the FE on the "Nueva acta"
    // type-picker card. Lets a recipe disambiguate types whose labels
    // alone aren't enough (residential's check-in vs check-out). Absent
    // for verticals with a single recurring type (facility's acta_visita).
    description: z.string().min(1).optional(),
    areaCatalogMode: areaCatalogModeSchema.default("shared_pool").optional(),
    // See milestoneFormSectionSchema above. Optional — only declared by
    // milestone reportTypes with a structured single-page form.
    milestoneForm: z.array(milestoneFormSectionSchema).optional(),
    // See assetAttributeSyncSchema above. Optional — only declared by
    // reportTypes whose sign event should back-fill Asset attributes.
    assetAttributeSync: assetAttributeSyncSchema.optional(),
    // How a report of this type comes into existence (ADR-0111):
    //   "guided_flow"        — the default: created open, walked through
    //                          captura → revisión → cierre.
    //   "standalone_finding" — born terminal from a single observation
    //                          submit (photos + note, AI-classified); the
    //                          creating transaction ends with the freeze +
    //                          closed_at write. The picker injects its fixed
    //                          option iff standaloneFindingReportType()
    //                          returns non-null — never a slug branch.
    entryMode: z.enum(["guided_flow", "standalone_finding"]).default("guided_flow").optional(),
    // Whether the clients offer to START (or resume) a report of this type
    // (#0699). Absent = true, so every existing reportType keeps its
    // behavior untouched. `false` keeps the type declared —existing reports
    // still validate, open read-only and render their PDF— but hides every
    // entry point that would begin one or carry an unfinished draft
    // forward: milestone card, "Nueva acta" picker, Retomar cards, the
    // detour of a new observation into an in-flight acta.
    //
    // Lives on the reportType and not as a vertical-wide boolean because
    // what gets hidden is one report type, not a capability: a recipe-level
    // "deliveryActEnabled" would name a postventa concept inside the engine
    // and every client would still have to work out which reportType it
    // meant. Clients read it through isReportTypeCreatable() below (or its
    // FE mirror), never through `recipe.slug`.
    creatable: z.boolean().optional(),
    // Per-lane exception to `creatable: false`: the observation-status
    // actor lanes (memberRoles[].observationStatusActor) whose members may
    // still start or resume a report of this type. Lanes, never role keys
    // (ADR-0019 / ADR-0090). Only meaningful when `creatable` is false —
    // the superRefine refuses it otherwise, since on a creatable type it
    // would silently read as a restriction that nothing enforces. Clients
    // read it through isReportTypeCreatableForRole() (or its FE mirror).
    creatableByActors: z.array(observationStatusActorSchema).min(1).optional(),
    // Where the asset's current state ("estado actual del activo") is read
    // from for reports of this type (ADR-0137). Explicit per reportType
    // (ADR-0019) because the three shapes have nothing in common at the
    // storage level:
    //   "checklist_items"     — report_checklist_results verdicts per item
    //                           (facility's acta_visita).
    //   "area_condition"      — a per-area condition (residential checks).
    //   "milestone_checklist" — answers of a milestoneForm checklist.
    // Absent = this report type does not feed the asset state. Positives
    // are derived at read time and never materialized as findings, so the
    // superRefine requires at least one severity with conformance
    // "positive" whenever any reportType declares a source.
    stateSource: z.enum(["checklist_items", "area_condition", "milestone_checklist"]).optional(),
    // Per-photo verdict review (ADR-0169): the AI proposes, per photo of a
    // checklist item, defecto / conforme / no_concluyente and a human
    // confirms each photo on the dashboard — an annotation layer that
    // never changes the signed acta. Absent = the report type has no photo
    // review. Valid only with stateSource "checklist_items" (superRefine):
    // the review set is the item's captured_photo_urls, which only that
    // source has. `autoAnnulOnAllConforme`: when the human confirmations
    // leave every photo of the item «conforme», the item's observation is
    // annulled through the regular lifecycle graph (same edge, note,
    // share-link revocation and email as a manual annul).
    photoVerdictReview: z.object({ autoAnnulOnAllConforme: z.boolean() }).strict().optional(),
});
const areaTemplateSchema = z.object({
    name: z.string().min(1),
    // repeatable=true means the area can appear multiple times in one report
    // (e.g. "Dormitorio" → "Dormitorio 1", "Dormitorio 2"). Non-repeatable
    // areas (Cocina, Living-comedor) appear at most once.
    repeatable: z.boolean(),
    // preselected=true means the recipe pre-creates this area when a new
    // report is opened. Other areas are added on demand from the recipe pool.
    preselected: z.boolean(),
});
const findingCategorySchema = z.object({
    key: slugSchema,
    label: z.string().min(1),
});
const severitySchema = z.object({
    key: slugSchema,
    label: z.string().min(1),
    // Severities have an implicit ordering from "ok" to "bad" used for
    // dashboards and prioritization. The recipe defines the order; the engine
    // doesn't assume one.
    rank: z.number().int().nonnegative(),
    // Badge → S-code, for rows that predate the P1/P2/P3 classification
    // and therefore carry no `derived_severity`. It is the OPPOSITE
    // direction of ADR-0129 §4's reserved `derivedFrom` (S-code → badge),
    // and deliberately a different field: `derivedFrom` says which badge
    // a derived S-code renders as; `legacyDisplayAs` says which S-code a
    // legacy badge is *displayed* as when nothing was derived. Display
    // only — `effectiveObservationSeverity` reads it, nothing persists
    // it (fitness: effective-severity-never-persisted). Optional so
    // verticals without the taxonomy declare nothing.
    legacyDisplayAs: z.enum(DERIVED_SEVERITIES).optional(),
    // Whether this level means "no problem" ("positive") or a defect of
    // some degree ("negative"), ADR-0137. Rank orders severities; it does
    // NOT say which one is positive — postventa's rank-0 `ok` («Estética»)
    // is a cosmetic defect. Absent reads as negative: only an explicit
    // "positive" counts, resolved through positiveSeverityKeys().
    conformance: z.enum(["positive", "negative"]).optional(),
});
const idDocumentTypeSchema = z.enum(["rut", "passport", "national_id", "none"]);
// Where this role's `idDoc` comes from at sign time. Recipe-driven so a
// b2c vertical (corredor's personal RUT) and a b2b vertical (organization's
// tax RUT) can express the same role differently. See ADR-0018.
//
//   "form"                 — captured at inspection time (default)
//   "session_user"         — auth user's rut (auth_users.rut)
//   "session_organization" — auth org's rut (organizations.rut)
const partyIdentitySourceSchema = z.enum(["form", "session_user", "session_organization"]);
const partyRoleSchema = z.object({
    key: slugSchema,
    label: z.string().min(1),
    required: z.boolean(),
    idDocument: idDocumentTypeSchema,
    // Optional. Default behaviour is "form" — the engine reads no source
    // hint and expects callers to populate `idDoc` themselves. Recipes
    // that opt in shift the responsibility onto the server's auth context.
    source: partyIdentitySourceSchema.optional(),
    // Whether this role captures a typed name at sign time. Optional;
    // absent means required (the engine treats anything other than an
    // explicit `false` as "name required", so every existing recipe keeps
    // its behaviour). Recipes that set it false (facility's jefe_tienda /
    // supervisor, ADR-0040) let the role sign with a stroke only — no typed
    // name, no idDoc — while the engine still enforces the name for every
    // other role. Sibling of `idDocument` / `source`: identity capture is a
    // recipe policy, not an engine default.
    nameRequired: z.boolean().optional(),
});
// How a declined signature captures its reason. Optional — when absent
// the engine accepts a declined signature with no reason payload (matches
// residential's `arrendatario` declinable today). When set, the engine
// requires a matching report-level constancia (see invariants
// `validateConstancia`). "enum_plus_optional_free_text" is the facility
// shape introduced in ADR-0028: a closed enum of motivos plus an optional
// free-text declineNote on the signature itself (used when motivo='otro').
const declineReasonCaptureSchema = z.enum(["enum_plus_optional_free_text"]);
const signatureRoleSchema = z.object({
    // Must match a partyRoles[].key. Validated cross-field by recipeSchema.
    partyRole: slugSchema,
    label: z.string().min(1),
    // declinable=true allows the signer to refuse without blocking the report.
    // v2 ships with `arrendatario` as declinable for the rental case.
    declinable: z.boolean(),
    // Optional — what shape of decline-reason payload the engine demands
    // when a declined signature is submitted for this role. Recipes that
    // omit it accept declined signatures with no companion data (legacy
    // residential behavior). Recipes that set it (facility ADR-0028)
    // require the constancia + optional declineNote pair.
    declineReasonCapture: declineReasonCaptureSchema.optional(),
});
// Recipe-declared values for `assets.status`. The engine validates an
// asset's status against this list; verticals that don't use the field
// declare an empty array. See Spec Arquitectural §2.6 / §3.5(g).
const assetStatusSchema = z.object({
    key: slugSchema,
    label: z.string().min(1),
});
// Recipe-declared vocabulary for findings.observation_status (ADR-0058).
// Deliberately its own primitive rather than a reuse of the finding's
// delegation_status vocabulary (provider-assignment workflow, ADR-0074)
// — the two model different lifecycles (warranty-remediation tracked
// by the inmobiliaria itself vs. third-party provider resolution) and
// reusing one for the other would force the FE to branch on which
// vocabulary a value belongs to. Empty array = vertical doesn't use
// asset-scoped observation status. `terminal` marks a status that
// closes the observation (e.g. "resuelta", "rechazada") — the FE uses
// it to decide whether an observation counts as "open" without a
// slug branch.
// Who may drive an observation to this status. Postventa share-link
// feature (council review): default ["session_user"] keeps every
// existing recipe's behavior unchanged — only a status that explicitly
// opts in becomes reachable from an unauthenticated share-link actor.
// This is the PRIMARY authorization gate (see
// resolveObservationStatus in invariants.ts); a status's `terminal`
// flag stays purely presentational and is never itself a security
// control, so a recipe author changing a badge's terminal/non-terminal
// bucket can never accidentally widen what a share link may set.
//
// "assigning_party" (ADR-0129, founder Option A, 2026-09-01) is a
// CAPABILITY LANE, not a role key: it names the authenticated callers
// a recipe has granted assignment authority via
// memberRoles[].observationStatusActor — the same declaration mechanism
// "asset_party" already uses. Role keys themselves never appear here
// (ADR-0090 stays intact: the role namespace is platform-global; the
// recipe only maps its roles onto lanes). The lane exists so a
// sensitive destination (postventa's "asignada") can exclude plain
// "session_user" at the node ceiling itself — protection by
// construction, inherited by every present and future write path that
// goes through resolveObservationStatus, instead of a per-route role
// check each new surface must remember to copy (#0567's failure shape).
// observationStatusActorSchema is declared above reportTypeSchema (it
// also types reportType.creatableByActors); the lane semantics described
// here are its authoritative documentation.
// ADR-0105 Decision 1 — one legal edge of the observation lifecycle
// graph: from the status that declares it, to `to`, drivable by the
// listed actors. The effective permission for a transition is the
// INTERSECTION of the destination's `settableBy` (node ceiling,
// ADR-0070 level 2, preserved verbatim) and the edge's `actors` — the
// edge only ever narrows, never widens.
const observationTransitionSchema = z.object({
    to: slugSchema,
    actors: z.array(observationStatusActorSchema).min(1),
    // ADR-0141 — a note demanded by THIS edge, not by the destination
    // node. Needed when two edges enter the same node with different
    // requirements: postventa's `rechazada_cerrada` is reached by the
    // owner's «Estoy de acuerdo» (no note) and by the jefe maintaining the
    // rejection after an appeal (note mandatory, mailed to the owner).
    // Node-level `requiresNote` cannot tell them apart. Enforced by
    // validateObservationTransition only when the transition travels a
    // declared edge; the ceiling-only fallback lane (NULL/retired origins)
    // skips edges and therefore skips this — same blind spot the fallback
    // has for the edge set itself, accepted by design.
    requiresNote: z.boolean().optional(),
});
// El mismo estado puede nombrarse distinto según a quién se le muestra.
// Hoy ninguna receta lo declara: broker_postventa lo usaba para que el
// propietario leyera `abierta` con otra palabra y `resuelta` como "Lista
// para revisar", y desde 2026-10-02 (fundador) esas son las palabras
// base para TODA audiencia, así que el override se retiró de la receta.
// El primitivo queda para una vertical que de verdad necesite dos voces.
//
// Vive en la receta y NO en un mapa del cliente por dos razones: el
// vocabulario visible de una vertical es dato de receta (misma regla que
// `labels` y que `tone`), y un mapa en el cliente sería una segunda
// representación del set de estados que se desincroniza en silencio
// cuando la receta agrega uno.
//
// Se teclea con el MISMO enum de actores que gobierna `settableBy` y las
// aristas — no una audiencia nueva paralela. Parcial a propósito: un
// actor sin override lee el `label` de siempre, así que las verticales
// que no lo declaran no cambian en nada.
const observationStatusLabelByActorSchema = z
    .record(observationStatusActorSchema, z.string().min(1))
    .optional();
const observationStatusSchema = z.object({
    key: slugSchema,
    label: z.string().min(1),
    labelByActor: observationStatusLabelByActorSchema,
    terminal: z.boolean().default(false),
    settableBy: z.array(observationStatusActorSchema).default(["session_user"]),
    // ADR-0105 Decision 1. Default [] keeps every flat vocabulary exactly
    // as it behaves today: the graph is enforced only when at least one
    // status in the vocabulary declares an edge (see
    // observation-lifecycle.ts). In a graph-enabled vocabulary, an empty
    // array on a LIVE status means terminal-by-absence-of-edges
    // (postventa's "rechazada") — deliberately not the presentational
    // `terminal` flag's job (ADR-0105 Decision 3).
    transitionsTo: z.array(observationTransitionSchema).default([]),
    // ADR-0105 Decision 2. A retired key resolves for display/projection
    // of persisted legacy rows but is never re-enterable (settableBy: [])
    // and has no outgoing edges; a finding sitting on a retired status
    // re-enters the live graph via the node-ceiling fallback documented
    // in observation-lifecycle.ts. Never delete a retired key — rows in
    // findings.observation_status still carry it.
    retired: z.boolean().default(false),
    // Visual tone for the status pill, recipe-DECLARED so no client ever
    // hardcodes a vertical's status keys into shared code (code-reviewer
    // finding, ola 2 — the tone map in dashboard util was slug-branching
    // with one extra step). Optional: absent falls back to the clients'
    // label-sentiment heuristic, so flat vocabularies need no change.
    tone: z.enum(["idle", "info", "brand", "warn", "danger", "ok", "crit"]).optional(),
    // Entering this status emails the unit's asset party (the owner of
    // the asset, `assets.owner_user_id`) — postventa's "aceptada" is the
    // first user: the inmobiliaria accepted the owner's observation and
    // will coordinate the repair. Recipe-declared so core-api never
    // compares a status key or a slug to decide who gets mail; absent =
    // no mail, which is every vertical that doesn't declare it (facility
    // and residential have no observation vocabulary at all). A status
    // re-entered from itself never re-sends (core-api checks from ≠ to).
    notifiesAssetParty: z.boolean().optional(),
    // Entering this status requires a note from the actor — postventa's
    // "rechazada" is the first user: the rejection is final and the reason
    // is what the propietario reads in the thread. Declared on the NODE,
    // not on an edge, so the fallback lane (NULL / retired origins, which
    // skips edges entirely) enforces it too. Enforced by
    // validateObservationTransition (OBSERVATION_NOTE_REQUIRED); absent =
    // a note is welcome but optional, which is every other status.
    requiresNote: z.boolean().optional(),
    // ADR-0134 §3 — entering this status requires evidence attached to the
    // milestone (at least one photo). Same pattern as `requiresNote`: it
    // lives on the destination NODE, not on the edge or the route, so every
    // write path that reaches the node inherits it. Facility declares it on
    // `resuelta` (the jefe de tienda resolving logged in must prove it);
    // postventa does not declare it — its `/attend` already requires proof
    // through its own lane (ADR-0073). Enforced by
    // validateObservationTransition from its `evidenceCount` input
    // (OBSERVATION_EVIDENCE_REQUIRED), fail-closed: a write path that does
    // not pass a count cannot reach the node at all, so the route that
    // carries the evidence is the only one that can. Absent = evidence
    // welcome but optional.
    requiresEvidence: z.boolean().optional(),
    // ADR-0141 — WHICH asset-party mail entering this status sends, when
    // `notifiesAssetParty` is on. Before this field core-api picked the
    // rejection mail by shape (`transitionsTo.length === 0`); the moment
    // `rechazada` gained edges that shape stopped meaning "rejection", so
    // the kind is now declared. Absent = the acceptance/avance mail, which
    // is every status that declared `notifiesAssetParty` before this field
    // except the rejections.
    notificationKind: z.enum(["accepted", "rejected"]).optional(),
    // ADR-0141 — this destination is the staff's cierre sin trabajo (the
    // dashboard's «Rechazar» / «Anular» button). Declared, not derived:
    // the old shape test (`terminal && transitionsTo.length === 0`) also
    // matches `rechazada_cerrada`, which is a closure of the owner's
    // response cycle, never a button the triage panel offers.
    rejectShaped: z.boolean().optional(),
});
// Vertical-specific configuration of the asset-creation form (/cartera/nuevo).
// The form's structure is identical across verticals — only the secondary
// identifier field (residential: "N°", facility: "N° de local") and
// the type-selector options differ. Stored on the recipe so the FE picks
// these up via /recipes/me without per-vertical screens.
const assetTypeOptionSchema = z.object({
    key: slugSchema,
    label: z.string().min(1),
});
const assetFormSchema = z.object({
    identifierField: z.object({
        label: z.string().min(1),
        required: z.boolean(),
    }),
    types: z.array(assetTypeOptionSchema).min(1),
});
// Vertical-specific addendum strings appended to the engine's base
// prompt for each AI task (shell-and-addendum model). Empty strings are
// fine — the base prompt alone is usable; the addendum just sharpens the
// output quality for the vertical's domain. ai-svc composes
// `<base prompt>\n\n<addendum>` at request time.
const aiPromptsSchema = z.object({
    analyzeAreaAddendum: z.string().default(""),
    summarizeReportAddendum: z.string().default(""),
    /** ADR-0025 per-bloque analysis addendum. Facility doesn't ship one
     *  today (the engine shell carries everything), but the seam is
     *  here so a future vertical can sharpen voice without an engine
     *  change. Defaults to empty — compose layer skips the trailing
     *  blank-line separator when absent. */
    analyzeBlockAddendum: z.string().default(""),
    /** ADR-0050 gerencial portfolio chat addendum. Same shell-and-addendum
     *  seam as the others — the engine shell carries the grounding rules,
     *  a vertical can add voice here. Facility ships it empty. */
    chatAddendum: z.string().default(""),
    /** Análisis de cartera con IA: el memo ejecutivo que narra hallazgos
     *  ya calculados. El shell del engine pone las reglas de honestidad
     *  (citar, no inventar cifras); la vertical pone su vocabulario y a
     *  quién le habla. Vacío en las verticales sin esa superficie. */
    portfolioBriefAddendum: z.string().default(""),
});
// Brand layer config — opt-in per ADR-0020. Only verticals where the
// hierarchy is `org → brand → group → asset` declare this (facility);
// arriendos and postventa omit the field entirely. The FE branches on
// `recipe.brands?.enabled` to decide whether to show the BrandStrip and
// brand-related filters — never on `recipe.slug`.
// Exported (not just its inferred type) so packages/api-types can derive
// wire schemas via `.pick()` off the real shape instead of a hand-written
// mirror — see `internalOrgMembershipPolicyResponseSchema` in
// packages/api-types/src/membership-policy.ts.
export const brandsConfigSchema = z.object({
    enabled: z.boolean(),
    // Display nouns for the brand entity. Distinct from labelsSchema so a
    // recipe can decide independently whether brands exist and what to call
    // them. When enabled is false, label/pluralLabel still parse but are
    // unused.
    label: z.string().min(1),
    pluralLabel: z.string().min(1),
});
// What level a membership row binds to. Mirrors the four nullable scope
// columns on auth_memberships (scope_brand_id / scope_asset_group_id /
// scope_asset_id, plus the "all NULL = organization scope" sentinel).
// See Phase 1 schema + ADR-0020.
const membershipScopeTypeSchema = z.enum(["organization", "brand", "asset_group", "asset"]);
// Recipe-declared membership roles. Every recipe declares its full role
// set + the scope level each role binds to. The engine uses this for
// auth_memberships.role enum validation (in conjunction with the DB CHECK
// constraint added in Phase 1) and the FE uses it to label dashboards.
//
// Rental's two roles (admin, inspector) declare scopeType: "organization"
// — preserves pre-ADR-0020 behavior. Facility's four roles (company_manager,
// brand_manager, store_manager, facility_supervisor) declare brand /
// asset_group / asset scopes respectively.
// Exported (not just its inferred type) so packages/api-types can derive
// wire schemas via `.pick()` off the real shape instead of a hand-written
// mirror — see `internalOrgMembershipPolicyResponseSchema` in
// packages/api-types/src/membership-policy.ts.
// ADR-0165 — where a role's visible brand ("its organization") comes from,
// kept apart from `scopeType` on purpose: scopeType is authorization (what
// the role may read), this is identity (whose brand the dashboard shows).
//
//   "affiliation" — the membership carries one explicit brand (the
//                   supervisor's), stored by core-api; its scope must stay
//                   inside that brand.
//   "scope"       — the brand is derived from the role's stores, and those
//                   stores may not span two brands.
//
// Absent = no brand identity of its own (derived as before, unguarded).
const brandIdentitySchema = z.enum(["affiliation", "scope"]);
export const memberRoleSchema = z.object({
    key: slugSchema,
    label: z.string().min(1),
    scopeType: membershipScopeTypeSchema,
    // Which observation-status actor bucket this role resolves to when
    // setting a finding's status (see resolveObservationStatus /
    // resolveMemberRoleActor). Optional rather than `.default(...)`
    // deliberately: recipe objects in packages/recipes/src/*.ts are
    // authored as plain `: Recipe`-typed literals, never round-tripped
    // through `recipeSchema.parse()`, so a zod `.default()` would only
    // widen the *type*'s output shape to require the key — breaking every
    // existing recipe literal's memberRoles entries at compile time — and
    // would never actually run to backfill a value. `resolveMemberRoleActor`
    // applies the "session_user" fallback at the one real call site
    // instead, which is both correct and TS-additive for every existing
    // recipe.
    observationStatusActor: observationStatusActorSchema.optional(),
    // ADR-0124 — la clave sigue DECLARADA pero ya no designa a nadie que
    // se invite hoy. Dos cosas dependen de la distinción y tiran para
    // lados opuestos:
    //
    //   - `resolveMemberRoleActor` DEBE seguir resolviendo una clave
    //     retirada (#0663): hay membresías vivas emitidas con ella, y
    //     fallar cerrado ahí dejaría fuera en caliente a esas cuentas.
    //   - `resolveAssetPartyMemberRole` (invariants.ts) debe IGNORARLA:
    //     pregunta "a qué rol invito al propietario de esta unidad", y
    //     una clave que nadie vuelve a emitir no es respuesta — sin este
    //     marcador el predicado sería ambiguo en broker_postventa, donde
    //     `propietario` y el legacy `store_manager` declaran ambos
    //     `scopeType: "asset"` + `observationStatusActor: "asset_party"`.
    //
    // Opcional sin `.default(false)` por el mismo motivo que
    // `observationStatusActor` arriba: las recetas son literales tipados
    // `: Recipe`, nunca pasan por `recipeSchema.parse()`, así que un
    // `.default()` sólo ensancharía el tipo de salida y nunca correría.
    retired: z.boolean().optional(),
    // ADR-0165 — see `brandIdentitySchema`. Optional without `.default()`
    // for the same reason as `observationStatusActor`. Declared per role,
    // not per scopeType: broker's `jefe_proyecto` is also "asset_group" and
    // has no brands at all. Read it through `brandIdentityOf`.
    brandIdentity: brandIdentitySchema.optional(),
});
// Per ADR-0020 + founder ruling 2026-05-23 (Decision 4 = "configuration,
// not content"). Controls how `recipe.findingCategories` is resolved
// against an org's `organizations.config.findingCategoriesOverride`:
//
//   "recipe_only"          — ignore any org override. Used by arriendos /
//                            postventa: their checklists are vertical-
//                            level, not per-org.
//   "recipe_with_override" — future feature; org may add/override entries
//                            keyed by `key`. Not implemented in v1 — falls
//                            back to "recipe_only" semantics until a
//                            recipe needs it.
//   "org_defined_only"     — when the org has a non-empty override array,
//                            full-replace the recipe defaults. Used by
//                            facility: each chain uploads its own
//                            checklist at onboarding.
const findingCategorySourceSchema = z.enum([
    "recipe_only",
    "recipe_with_override",
    "org_defined_only",
]);
// The 5 recipe primitives — the "API of a vertical" (Spec Arquitectural
// §2.3). Any new vertical is described in these terms. Frontend reads
// these to decide what cartera renders, whether the "+ new asset" CTA is
// visible, etc. No service infers behaviour from the recipe slug.
const rootEntitySchema = z.enum(["asset", "group"]);
// Capture-flow primitive (ADR-0025). Distinguishes the lineal flow
// (residential / postventa: capture all areas → process whole report →
// review → sign) from the A→B-per-bloque flow (facility: per-bloque
// capture A → ai analyze block → review B → next block → … → sign).
// FE branches on this primitive, never on `recipe.slug` (fitness function
// `no-recipe-slug-branches`).
const captureFlowSchema = z.enum(["linear", "block_a_b"]);
const assetCreationModeSchema = z.enum(["self_serve", "batch_only"]);
// ADR-0087. Governs system-initiated lookups of an already-persisted
// `asset.address` against a third-party mapping processor (Google) — the
// in-app map screen's address-derived Static Maps fallback for assets
// with no persisted coordinates. Does NOT govern user-initiated Places
// autocomplete at write time (the user types the address and picks a
// result in an active session) or a user-tapped "open in Maps" deep
// link — both are the data subject's own agent choosing to navigate,
// not a lookup this app performs on its own.
//
// Optional, no default — absent means "none" (no third-party lookup).
// A `rootEntity: "group"` recipe's asset address is not always business
// data: facility_management's is a commercial store; broker_postventa's
// is a real buyer's home. The map route's only prior gate
// (`rootEntity !== "group"`) can't tell the two apart, and a
// `recipe.slug` branch is forbidden (ADR-0019, enforced by
// scripts/fitness/no-recipe-slug-branches.mjs). This primitive is the
// gate; every recipe that wants the fallback must say so explicitly.
const assetAddressLookupSchema = z.enum(["third_party", "none"]);
const areaSelectionModeSchema = z.enum(["required", "optional", "disabled"]);
// Group-shape config — only present on `rootEntity: "group"` recipes
// (postventa, facility). Tells the cartera screens (Sesión 4 Tanda D)
// what kind label to put on the GroupCard (`kind`), what to surface in
// the card's subline (`sublineSource`), and the noun for the strip cifra
// (`assetCountLabel`).
//
// `kind` is recipe-driven and validated against the `asset_groups.kind`
// column on writes — POST /asset-groups rejects a body whose `kind`
// doesn't match the active recipe's `group.kind`.
const groupConfigSchema = z.object({
    // Slug for the kind (`"proyecto"` for postventa, `"zona"` for
    // facility). Stored on every asset_groups row.
    kind: slugSchema,
    // Where the cartera's GroupCard subline reads from. Sesión 4 Patch
    // S4.2 #11: one cifra + one direccional per card.
    //   "address"    → asset_groups.address_line ("Av. Mirador 4500, Las Condes")
    //   "supervisor" → resolved displayName from auth_users via the
    //                  in-memory cache + auth-svc lookup. Falls back to
    //                  "Supervisor" if the lookup hasn't landed yet.
    //   "none"       → subline omitted from the response.
    sublineSource: z.enum(["address", "supervisor", "none"]),
    // Plural noun for the strip cifra ("propiedades" for postventa,
    // "tiendas" for facility). Rendered verbatim — recipes that want a
    // different word just change this string.
    assetCountLabel: z.string().min(1),
});
// Cartera-home shape primitive — ADR-0023. Discriminates within
// `rootEntity: "group"` between postventa's GroupCard listing and
// facility's flat-asset "Mis tiendas" listing. Recipes with
// `rootEntity: "asset"` (arriendos) omit the field; the FE falls
// back to `rootEntity` semantics. Governs the *mobile* cartera home
// only — see `dashboardsConfigSchema.cartera` below (ADR-0072) for
// the unrelated desktop-dashboard opt-in.
const carteraConfigSchema = z.object({
    homeLayout: z.enum(["group_list", "flat_assets"]),
    // Where the mobile "Nueva acta" CTA may be started from — ADR-007X.
    // "root"       → sticky CTA shown on /cartera and /cartera/[id]
    //                (facility; also the default for a group recipe
    //                that omits this field, so it fails open rather
    //                than silently losing its only entry point).
    // "asset_only" → CTA hidden on both group-level screens; the
    //                asset-detail screen is the only entry point
    //                (postventa — an acta always belongs to a unit).
    // Deliberately its own field, not derived from `homeLayout`: that
    // primitive answers a layout-shape question, not an entry-point
    // policy question, and ADR-0023 already rejected reusing a
    // primitive across an unrelated axis as "coincidental, not
    // semantic" (see Options §1 there). Same reasoning applies here.
    newActaEntryPoint: z.enum(["root", "asset_only"]).default("root"),
});
// Which gerencial dashboard surfaces this vertical opts into. Retires
// the `recipe.slug !== "facility_management"` branch in
// facility-dashboard.ts (a pre-dated fitness-function exception) —
// the right shape was always a primitive, per that route's own
// tech-debt comment. Optional and per-surface (not a single boolean)
// so a future vertical can opt into one dashboard without implying it
// gets every other one. Verticals that opt into none omit the field
// entirely, same posture as `brands`.
const dashboardsConfigSchema = z.object({
    facility: z.boolean().default(false),
    // Desktop gerencial "Proyectos y Unidades" cartera surface (ADR-0072).
    // Distinct from `carteraConfigSchema` above (ADR-0023) — that one
    // governs the *mobile* cartera home layout; this one governs whether
    // the *desktop dashboard* gets a cartera surface at all. Similar
    // names, unrelated questions — don't conflate them.
    cartera: z.boolean().default(false),
});
// Base shape — exported so wire schemas (api-types/recipes.ts) can
// `.omit()` the server-only fields without re-declaring the shape.
// The refined `recipeSchema` below adds cross-field invariants
// (unique keys, signatureRole→partyRole links) on top.
// The signed-acta PDF's vertical-specific wording, declared as data so the
// renderer never branches on `recipe.slug` — same posture as severities /
// findingCategories / milestoneForm.
//
// Everything the PDF says about *what kind of document this is* lives here.
// Nouns for the inspected thing and its areas do not: those already exist as
// `labels.asset` / `labels.area` and are read from there.
//
// Before this primitive the renderer hardcoded residential's wording, so a
// facility acta for a retail store rendered "Entrega de inmueble en arriendo",
// a "Declaración de entrega" about a propietario handing keys to an
// arrendatario, and comparecientes cards for roles facility does not have.
const actaDocSchema = z.object({
    // Portada title, rendered as two lines (roman lead, italic emphasis).
    titleLead: z.string().min(1),
    titleEmphasis: z.string().min(1),
    // First portada meta cell — "Fecha de entrega" vs "Fecha de visita".
    dateLabel: z.string().min(1),
    // § 2 heading. Explicit per vertical rather than composed from
    // `labels.asset`, because Spanish gender and article vary with the noun
    // ("del inmueble", "de la tienda").
    subjectTitle: z.string().min(1),
    // § 6 heading, over the per-area findings blocks.
    inventoryTitle: z.string().min(1),
    // § 1 intro sentence. Supports {lugar} and {fecha} placeholders.
    partiesIntro: z.string().min(1),
    // § 3 paragraphs. An empty array omits the section — facility inspections
    // have nothing to declare about handing over property.
    declaration: z.array(z.string().min(1)).default([]),
});
export const recipeBaseSchema = z.object({
    slug: slugSchema,
    labels: labelsSchema,
    // Optional so a recipe predating this primitive still parses; the PDF
    // renderer falls back to its historical residential wording when absent.
    // `recipes.test.ts` asserts every registered recipe declares it, so a new
    // vertical fails the suite rather than silently inheriting arriendo copy.
    acta: actaDocSchema.optional(),
    rootEntity: rootEntitySchema,
    assetCreationMode: assetCreationModeSchema,
    areaSelectionMode: areaSelectionModeSchema,
    publicLinkEnabled: z.boolean(),
    reportTypes: z.array(reportTypeSchema).min(1),
    defaultAreas: z.array(areaTemplateSchema),
    findingCategories: z.array(findingCategorySchema).min(1),
    severities: z.array(severitySchema).min(1),
    // Empty array = vertical does not use the asset_status field. Otherwise
    // every value an asset can take must appear here. Cross-validated at
    // asset-write time, not at recipe-load time.
    assetStatuses: z.array(assetStatusSchema).default([]),
    // Empty array = vertical does not use asset-scoped observation
    // lifecycle (ADR-0058, broker_postventa). See observationStatusSchema.
    observationStatuses: z.array(observationStatusSchema).default([]),
    // Qué carrusel de primer acceso le toca a esta vertical, y a su
    // contraparte asset-scoped si tiene una.
    //
    // Reemplaza el proxy que el dispatcher venía usando —`brands.enabled`,
    // que significa «esta org es facility» sólo porque hoy es la única
    // receta con marcas encendidas (ADR-0020)— y que el propio archivo
    // denunciaba como deuda: una tercera variante por ese camino habría
    // exigido un chequeo por slug, que la fitness prohíbe.
    //
    // `assetPartyVariant` existe porque el onboarding del propietario no es
    // de la vertical sino del ROL: en postventa el jefe de proyecto y el
    // propietario entran a la misma receta y no comparten primer acceso.
    onboarding: z
        .object({ variant: slugSchema, assetPartyVariant: slugSchema.optional() })
        .optional(),
    // El cierre por conformidad. Es un HITO, no un sexto estado: la
    // contraparte que recibió el trabajo lo acepta y se estampa el overlay
    // (`observation_closed_at` / `_reason` / `observation_cycle`) sin que
    // `observation_status` se mueva. Por eso no vive en el grafo de
    // estados — el grafo no sabe expresar "cerrada sin cambiar de nodo".
    //
    // `fromStatus` es el estado desde el cual se ofrece la conformidad y
    // `closedBy` quién puede darla; la arista de la NO conformidad
    // (`resuelta → abierta`) sí vive en el grafo, porque esa sí mueve el
    // estado.
    //
    // `autoApproveDays` es el cierre por falta de respuesta (ADR-0142):
    // días corridos en America/Santiago desde que la observación entra en
    // `fromStatus` hasta que el job programado de core-api la cierra con
    // `observation_closed_reason = 'auto_approval'`. AUSENTE = nunca se
    // cierra sola (facility). Cerrar sola es una decisión automatizada con
    // efecto sobre el titular (Ley 21.719 art. 8° bis): por eso exige
    // `reminderDays` (los avisos previos; el job nunca cierra si alguno no
    // se envió bien) y `reopenWindowDays` (la revisión posterior: el
    // propietario la reabre con un toque durante esa ventana). El
    // superRefine exige las tres juntas. Ningún cliente calcula fechas con
    // estos números: el servidor proyecta `autoApprovalAt` y
    // `reopenableUntil` en el wire.
    //
    // `archiveRejectedAfterDays` es un reloj DISTINTO del anterior: gobierna
    // cuándo una observación rechazada sale de la lista activa, no cuándo
    // se aprueba sola. Confundirlos es el error fácil. AUSENTE = una
    // rechazada nunca sale sola de la lista activa (el estado del MVP,
    // #0700); es un filtro de lectura del cliente, no una transición.
    //
    // `closedBy` is one lane or a non-empty list of lanes (ADR-0134 §3:
    // facility closes from both the jefe de tienda and the supervisor).
    // Never read it directly — `closureLanes(recipe)` normalizes it to a
    // list and every consumer compares by inclusion.
    //
    // `notConformEvidenceRequiredFor` (ADR-0134 §5) lists the closure lanes
    // whose "No conforme" must carry the full constancia — at least one
    // photo plus the voice note's transcript. A closure lane NOT listed
    // still owes a motivo (transcript or written note), with photos
    // optional. ABSENT = every closure lane owes the full constancia,
    // which is exactly the pre-ADR-0134 behavior (postventa: the
    // propietario, its only closure lane). An EMPTY list is rejected by the
    // superRefine: it would silently exempt every lane, and "absent" is
    // already the way to say "default". Resolved by
    // `notConformEvidenceRequired(recipe, lane)`.
    observationClosure: z
        .object({
        fromStatus: slugSchema,
        closedBy: z.union([
            observationStatusActorSchema,
            z.array(observationStatusActorSchema).min(1),
        ]),
        notConformEvidenceRequiredFor: z.array(observationStatusActorSchema).optional(),
        autoApproveDays: z.number().int().positive().max(365).optional(),
        reminderDays: z.array(z.number().int().positive()).min(1).optional(),
        reopenWindowDays: z.number().int().positive().max(365).optional(),
        archiveRejectedAfterDays: z.number().int().positive().optional(),
    })
        .optional(),
    // ADR-0134 §6 — the default plazo (calendar days, «días corridos») an
    // assignment gets per severity, keyed by this recipe's own severity
    // keys. Absent = the engine's S1–S4 table (`defaultPlazoDiasFor`,
    // postventa's founder values), which only applies to verticals that
    // derive S-codes. Facility declares crítica/moderada/leve → 3/7/10.
    // Resolved by `observationDueDaysFor`. The superRefine below requires
    // the table to cover EXACTLY the declared severity keys: an unknown key
    // never matches a row, and a missing one would silently fall back to
    // the S-code table, which a vertical without S-codes does not use.
    observationDueDays: z.record(slugSchema, z.number().int().positive()).optional(),
    // ADR-0129 — the single gate that turns the postventa classification
    // taxonomy on for a vertical (patrón entryMode/observationClosure:
    // one primitive, resolved by engine helpers, zero slug branches).
    // Absent = the vertical has no classification taxonomy: the
    // /classification route 409s, no taxonomy column is ever written.
    // `version` names the catalog entry in the injected TaxonomyRegistry
    // that NEW classifications are stamped with; historical rows decode
    // against the version stamped on the row itself. Only the version
    // string lives here — the catalog CONTENT never rides the recipe
    // (recipeMeResponseSchema derives by omit(), and the propietario
    // must never receive taxonomy codes: spec invariant 8).
    observationTaxonomy: z.object({ version: z.string().min(1) }).optional(),
    // Undefined = vertical does not assign a per-observation folio
    // (ADR-0059, broker_postventa). When set, core-api stamps
    // `${folioPrefix}-${year}-${seq}` on every finding at insert time —
    // this is policy (which prefix); the sequencing mechanism itself is
    // engine/core-api, not recipe, concern.
    folioPrefix: z.string().trim().min(1).max(10).optional(),
    // ADR-0065, enmendado 2026-09-12 — qué estados de unidad habilitan a
    // un asset a recibir observaciones sueltas por
    // POST /assets/:id/observations. Undefined = sin gate.
    //
    // Reemplaza a `observationsRequireSignedMilestone`, que preguntaba por
    // un acta firmada. La pregunta cambió de eje: el invariante real nunca
    // fue "existe el PDF" sino "esta unidad fue entregada" — es decir, hay
    // una fecha de entrega y un propietario conocido. Atarlo al acta
    // dejaba fuera a toda unidad entregada antes de que existiera el
    // software, y el acta dejó de ser el core del producto (minutas 03 y
    // 04): es un add-on, no una puerta.
    //
    // La activación de la unidad —hoy, la invitación al propietario— es lo
    // que mueve `assets.attributes.asset_status` a un valor de esta lista.
    // Los valores deben existir en `assetStatuses`. Un asset sin
    // `asset_status` lee como el primer estado declarado y por lo tanto
    // falla cerrado si ése no está en la lista.
    //
    // Mecanismo en assertObservationEntryAllowed() (invariants.ts); este
    // campo es política, nada más (split ADR-0018).
    observationsRequireAssetStatusIn: z.array(slugSchema).optional(),
    // ADR-0153 (#0750) — las encuestas del propietario. AUSENTE = la
    // vertical no encuesta: ni el tick del NPS ni la conformidad crean
    // filas en `owner_surveys`, y el panel del dashboard responde
    // `enabled:false`. Sólo tiene sentido con una contraparte EXTERNA
    // (`assetPartyBinding(recipe).audience === "external"`): core-api exige
    // las dos cosas, así que facility y arriendos quedan fuera sin rama por
    // slug.
    //   - `npsWaves`: qué olas del NPS salen (a los 2 y a los 9 meses de la
    //     entrega). Una lista vacía apaga el NPS y deja la visita.
    //   - `visit`: si la conformidad «conforme» del propietario pregunta
    //     «¿Cómo fue la visita técnica?».
    ownerSurveys: z
        .object({
        npsWaves: z.array(ownerSurveyNpsKindSchema),
        visit: z.boolean(),
    })
        .optional(),
    // ADR-0124 — el estado al que la ACTIVACIÓN de una unidad la mueve.
    // Postventa declara "en_garantia"; rental y facility no lo declaran, y
    // `POST /assets/:id/owner-invitation` responde 422
    // RECIPE_HAS_NO_ACTIVATION sin tocar ninguna fila.
    //
    // Existe para que el estado destino salga de la receta y no de un
    // literal en core-api: sin este primitivo el handler tendría que
    // escribir `"en_garantia"`, que es una rama por slug lavada a través
    // de una constante (§0.6 invariante 4/8, fitness
    // `activation-status-never-literal-in-core-api`).
    //
    // El superRefine de abajo lo valida contra `assetStatuses` y, cuando
    // la receta declara `observationsRequireAssetStatusIn`, contra esa
    // lista también: activar a un estado que el gate de observaciones no
    // admite dejaría la unidad "activada" y muda a la vez.
    activationAssetStatus: slugSchema.optional(),
    // Which supervisor_verdict values (report_checklist_results) should be
    // materialized into a `findings` row at sign time. Undefined/empty =
    // vertical doesn't use the checklist capture flow, or uses it but its
    // management surface reads report_checklist_results directly rather
    // than `findings`. facility_management declares ["no_cumple","con_obs"]
    // — the two verdict values that represent an open observation. Policy
    // only; the materialization mechanism lives in the engine
    // (materializeChecklistFindings, score.ts) and the write happens
    // co-transactionally with /sign (ADR-0066).
    checklistVerdictsMaterializeFindings: z.array(z.string()).default([]),
    assetForm: assetFormSchema,
    partyRoles: z.array(partyRoleSchema).min(1),
    signatureRoles: z.array(signatureRoleSchema),
    // ADR-0098 Decision 1 — which closure mechanism this vertical uses.
    // "signature" (default) requires signatureRoles to be non-empty;
    // "attestation" (facility_management) requires signatureRoles to be
    // empty — the report closes via POST /reports/:id/close, author-only,
    // with no counterparty signature. Cross-validated below (the
    // superRefine), not here, because it must read against
    // signatureRoles.length. Optional + default so every pre-existing
    // recipe keeps its current (signature) behavior without being touched.
    // Route registration and every FE consequence gate on THIS primitive,
    // never on recipe.slug (§0.6 invariant 4/8).
    reportClosure: z.enum(["signature", "attestation"]).default("signature").optional(),
    // The legal declaration shown under the signature pads on /firmar.
    // Optional and recipe-driven (ADR-0019): residential / postventa carry
    // the "ambas partes declaran…" text; facility omits it (a retail store
    // visit is not an "inmueble" declaration between two named parties).
    // When absent the FE renders no footnote.
    signatureDeclaration: z.string().optional(),
    aiPromptProfile: slugSchema,
    aiPrompts: aiPromptsSchema.default({
        analyzeAreaAddendum: "",
        summarizeReportAddendum: "",
        analyzeBlockAddendum: "",
        chatAddendum: "",
        portfolioBriefAddendum: "",
    }),
    pdfTemplate: slugSchema,
    // Required when `rootEntity === "group"` — cross-field invariant in
    // the refined schema below. Optional here so `rootEntity: "asset"`
    // recipes (arriendos) can omit it.
    group: groupConfigSchema.optional(),
    // ADR-0023 — controls the shape of the cartera home for
    // group-rooted recipes. Optional: `rootEntity: "asset"` recipes
    // omit it (arriendos' flat-asset home is implied by rootEntity).
    cartera: carteraConfigSchema.optional(),
    // Brand layer config — opt-in per ADR-0020. Verticals without brands
    // omit the field entirely; the FE branches on `recipe.brands?.enabled`.
    brands: brandsConfigSchema.optional(),
    // Gerencial dashboard opt-in (postventa share-link feature, S4). See
    // dashboardsConfigSchema above. Optional — verticals with no
    // gerencial dashboard surface omit the field entirely.
    dashboards: dashboardsConfigSchema.optional(),
    // Membership roles declared by this vertical, with the scope level
    // each role binds to. Required — every recipe must enumerate its
    // roles (no implicit "everyone is admin"). Cross-validated below: if
    // `brands.enabled === true`, at least one role must scope to "brand".
    memberRoles: z.array(memberRoleSchema).min(1),
    // Where `findingCategories` are sourced from per-org. Required — every
    // recipe declares its policy explicitly. Drives `resolveRecipe()` and
    // the AI compose layer.
    findingCategorySource: findingCategorySourceSchema,
    // Capture flow shape (ADR-0025). Optional with default "linear" so
    // existing recipes don't need to be touched; facility opts into
    // "block_a_b". FE branches on this; engine routes per-bloque jobs
    // (JOB_ANALYZE_BLOCK) only when set to "block_a_b".
    captureFlow: captureFlowSchema.default("linear").optional(),
    // Field-level write authorization (ADR-0051). Keys inside
    // `asset.attributes` that only org-scoped roles (admin / company_manager)
    // may write. Asset-scoped app roles (store_manager / facility_supervisor)
    // may read them but PUT /assets/:id strips them from the payload and
    // preserves the stored value. Default [] keeps arriendos / postventa
    // fully editable. Facility locks `jefe_de_tienda` (and the coupled
    // identity keys that drive the ADR-0036 invitation backfill). The FE
    // reads this same list to render the assetInfoField read-only — one
    // declarative source, never a recipe.slug branch.
    protectedAttributeKeys: z.array(z.string().min(1)).default([]),
    // ADR-0087 — see assetAddressLookupSchema above. Optional; absent means
    // "none". facility_management is, today, the only recipe declaring
    // "third_party".
    assetAddressLookup: assetAddressLookupSchema.optional(),
    // ADR-0144 — la unidad lleva una ficha canónica en `assets.attributes`
    // (tipología, estacionamientos, bodegas, fechas DOM / CBR / entrega;
    // ver unit-profile.ts) y el proyecto, contactos de emergencia. Ausente
    // = la vertical no tiene ficha: GET /assets/:id no proyecta ficha,
    // garantías ni contactos. `legalWarranties` enciende el cálculo de
    // garantías por partida desde los pisos del art. 18 de la LGUC.
    // Cuando está presente, las claves de la ficha tienen que estar en
    // `protectedAttributeKeys` (superRefine): sólo el importador y el
    // staff las escriben, nunca el propietario.
    unitProfile: z.object({ legalWarranties: z.boolean() }).strict().optional(),
});
export const recipeSchema = recipeBaseSchema.superRefine((recipe, ctx) => {
    const ensureUniqueKeys = (items, path) => {
        const seen = new Set();
        for (let i = 0; i < items.length; i++) {
            const key = items[i].key;
            if (seen.has(key)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [path, i, "key"],
                    message: `duplicate key "${key}"`,
                });
            }
            seen.add(key);
        }
    };
    ensureUniqueKeys(recipe.reportTypes, "reportTypes");
    ensureUniqueKeys(recipe.findingCategories, "findingCategories");
    ensureUniqueKeys(recipe.severities, "severities");
    ensureUniqueKeys(recipe.partyRoles, "partyRoles");
    ensureUniqueKeys(recipe.assetStatuses, "assetStatuses");
    ensureUniqueKeys(recipe.observationStatuses, "observationStatuses");
    ensureUniqueKeys(recipe.assetForm.types, "assetForm.types");
    ensureUniqueKeys(recipe.memberRoles, "memberRoles");
    // creatableByActors is an exception to `creatable: false` and nothing
    // else: on a type that is creatable for everyone it would read as a
    // restriction no client or route applies.
    recipe.reportTypes.forEach((rt, i) => {
        if (rt.creatableByActors !== undefined && rt.creatable !== false) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["reportTypes", i, "creatableByActors"],
                message: `reportType "${rt.key}" declares creatableByActors without creatable: false`,
            });
        }
    });
    // milestoneForm section keys, and each section's own item/field/
    // meter keys, must be unique within their scope.
    for (let rtIdx = 0; rtIdx < recipe.reportTypes.length; rtIdx++) {
        const form = recipe.reportTypes[rtIdx].milestoneForm;
        if (!form)
            continue;
        ensureUniqueKeys(form, `reportTypes.${rtIdx}.milestoneForm`);
        for (let sIdx = 0; sIdx < form.length; sIdx++) {
            const section = form[sIdx];
            const base = `reportTypes.${rtIdx}.milestoneForm.${sIdx}`;
            if (section.kind === "fields") {
                ensureUniqueKeys(section.fields, `${base}.fields`);
            }
            else if (section.kind === "checklist") {
                ensureUniqueKeys(section.items, `${base}.items`);
                ensureUniqueKeys(section.options, `${base}.options`);
            }
            else if (section.kind === "meters") {
                ensureUniqueKeys(section.meters, `${base}.meters`);
            }
        }
    }
    // Default areas must be unique by name — repeatable handles plurality.
    const areaNames = new Set();
    for (let i = 0; i < recipe.defaultAreas.length; i++) {
        const name = recipe.defaultAreas[i].name;
        if (areaNames.has(name)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["defaultAreas", i, "name"],
                message: `duplicate area "${name}"`,
            });
        }
        areaNames.add(name);
    }
    // "predefined_per_asset" fetches its catalog from a per-asset table
    // (asset_area_templates) keyed by assetId — that presumes assets are
    // batch-loaded under a group (see ADR-0056), so a recipe with
    // rootEntity: "asset" cannot declare it on any reportType.
    if (recipe.rootEntity !== "group") {
        for (let i = 0; i < recipe.reportTypes.length; i++) {
            if (recipe.reportTypes[i].areaCatalogMode === "predefined_per_asset") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["reportTypes", i, "areaCatalogMode"],
                    message: `"predefined_per_asset" requires rootEntity: "group"`,
                });
            }
        }
    }
    // Every signatureRole.partyRole must point at an existing partyRole.
    const partyKeys = new Set(recipe.partyRoles.map((r) => r.key));
    for (let i = 0; i < recipe.signatureRoles.length; i++) {
        const role = recipe.signatureRoles[i];
        if (!partyKeys.has(role.partyRole)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["signatureRoles", i, "partyRole"],
                message: `signatureRole references unknown partyRole "${role.partyRole}"`,
            });
        }
    }
    // ADR-0098 Decision 1 — reportClosure and signatureRoles must agree.
    // Coalesced explicitly rather than read as `recipe.reportClosure`
    // directly: with `.default("signature").optional()`, Zod's
    // ZodOptional short-circuits on `undefined` BEFORE ZodDefault runs,
    // so an omitted field parses to `undefined`, not `"signature"` — a
    // bare read here would silently miss the omitted-field case (see
    // scripts/fitness/recipe-closure-consistency.mjs, which documents and
    // coalesces the same way for its own scan).
    {
        const effectiveClosure = recipe.reportClosure ?? "signature";
        if (effectiveClosure === "signature" && recipe.signatureRoles.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["signatureRoles"],
                message: 'reportClosure "signature" requires at least one signatureRole — use reportClosure: "attestation" for a recipe with no signature collection',
            });
        }
        if (effectiveClosure === "attestation" && recipe.signatureRoles.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["signatureRoles"],
                message: 'reportClosure "attestation" requires an empty signatureRoles — attestation closure collects no signatures',
            });
        }
    }
    // `rootEntity: "group"` recipes (postventa, facility) must declare a
    // group config so the cartera knows how to render GroupCard subline,
    // strip cifra and `asset_groups.kind` validation. `rootEntity:
    // "asset"` recipes (arriendos) leave it undefined.
    if (recipe.rootEntity === "group" && !recipe.group) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["group"],
            message: 'recipe with rootEntity="group" must declare a `group` config (kind, sublineSource, assetCountLabel)',
        });
    }
    // ADR-0111 — a standalone_finding reportType is only coherent under
    // attestation closure: it is born with closed_at set and signed_at
    // permanently NULL, which a "signature" recipe would render as an
    // unsigned artifact claiming closure (ADR-0098's rejected Option G).
    // Same ZodOptional/ZodDefault coalescing caveat as reportClosure above.
    {
        const standalone = recipe.reportTypes.filter((t) => (t.entryMode ?? "guided_flow") === "standalone_finding");
        const effectiveClosure = recipe.reportClosure ?? "signature";
        if (standalone.length > 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["reportTypes"],
                message: 'at most one reportType may declare entryMode "standalone_finding"',
            });
        }
        for (const t of standalone) {
            if (effectiveClosure !== "attestation") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["reportTypes"],
                    message: `reportType "${t.key}" declares entryMode "standalone_finding", which requires reportClosure "attestation" — a born-closed report under signature closure would claim a closure no mechanism performed`,
                });
            }
            if (t.milestone) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["reportTypes"],
                    message: `standalone_finding reportType "${t.key}" must declare milestone: false`,
                });
            }
            if (t.areaCatalogMode !== undefined && t.areaCatalogMode !== "shared_pool") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["reportTypes"],
                    message: `standalone_finding reportType "${t.key}" must not declare an areaCatalogMode — it never enters capture`,
                });
            }
        }
    }
    // ADR-0124 — los tres asserts de la activación de unidades.
    //
    // (1) y (2) validan el estado destino: `activationAssetStatus` tiene
    // que existir en `assetStatuses` (si no, la activación escribiría un
    // valor que la propia receta no reconoce) y, cuando la receta declara
    // un gate de observaciones, tiene que estar dentro de él (si no, la
    // unidad quedaría activada y sin poder recibir observaciones — el
    // estado exacto que la activación existe para abrir).
    //
    // (3) valida la resolución del rol invitado: `resolveAssetPartyMemberRole`
    // devuelve el ÚNICO memberRole no retirado que combina
    // `observationStatusActor: "asset_party"` con `scopeType: "asset"`. Dos
    // candidatos vivos harían ambigua esa resolución, y la ambigüedad no
    // se manifestaría como error sino como una invitación al rol
    // equivocado — en broker_postventa, al `store_manager` legacy que
    // pertenece a los cuatro conjuntos de capacidad de api-types.
    if (recipe.activationAssetStatus !== undefined) {
        const statusKeys = new Set(recipe.assetStatuses.map((s) => s.key));
        if (!statusKeys.has(recipe.activationAssetStatus)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["activationAssetStatus"],
                message: `activationAssetStatus "${recipe.activationAssetStatus}" is not declared in assetStatuses`,
            });
        }
        const gate = recipe.observationsRequireAssetStatusIn;
        if (gate !== undefined && !gate.includes(recipe.activationAssetStatus)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["activationAssetStatus"],
                message: `activationAssetStatus "${recipe.activationAssetStatus}" is not in observationsRequireAssetStatusIn — activating a unit would leave it unable to receive observations`,
            });
        }
    }
    if (recipe.unitProfile !== undefined) {
        const missing = UNIT_PROFILE_PROTECTED_KEYS.filter((k) => !recipe.protectedAttributeKeys.includes(k));
        if (missing.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["protectedAttributeKeys"],
                message: `unitProfile is declared but protectedAttributeKeys misses ${missing.join(", ")} — the owner could rewrite the unit profile (ADR-0144 §1)`,
            });
        }
    }
    {
        const liveAssetParty = recipe.memberRoles.filter((r) => r.retired !== true && r.observationStatusActor === "asset_party" && r.scopeType === "asset");
        if (liveAssetParty.length > 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["memberRoles"],
                message: `at most one non-retired memberRole may declare observationStatusActor "asset_party" with scopeType "asset" — found ${liveAssetParty.length} (${liveAssetParty.map((r) => r.key).join(", ")}); mark the legacy one(s) retired: true`,
            });
        }
    }
    // ADR-0134 — the two closure/plazo primitives must name things the
    // recipe actually declares: a per-lane evidence rule for a lane that
    // cannot close is dead configuration, and a plazo for an undeclared
    // severity key would never match a row.
    if (recipe.observationClosure?.notConformEvidenceRequiredFor !== undefined) {
        const lanes = closureLanes(recipe);
        if (recipe.observationClosure.notConformEvidenceRequiredFor.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["observationClosure", "notConformEvidenceRequiredFor"],
                message: "notConformEvidenceRequiredFor must not be empty — omit it to keep the default (every closure lane)",
            });
        }
        recipe.observationClosure.notConformEvidenceRequiredFor.forEach((lane, i) => {
            if (!lanes.includes(lane)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["observationClosure", "notConformEvidenceRequiredFor", i],
                    message: `lane "${lane}" is not in observationClosure.closedBy`,
                });
            }
        });
    }
    // ADR-0142 — el cierre por falta de respuesta va completo o no va: un
    // cierre sin avisos previos o sin ventana de reapertura no cumple las
    // garantías de ADR-0105 §Decision 7. Los avisos son estrictamente
    // crecientes y caen antes del cierre.
    const closure = recipe.observationClosure;
    if (closure !== undefined) {
        const parts = [closure.autoApproveDays, closure.reminderDays, closure.reopenWindowDays];
        const declared = parts.filter((p) => p !== undefined).length;
        if (declared !== 0 && declared !== parts.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["observationClosure"],
                message: "autoApproveDays, reminderDays and reopenWindowDays must be declared together (ADR-0142)",
            });
        }
        if (closure.reminderDays !== undefined) {
            closure.reminderDays.forEach((day, i) => {
                const prev = i > 0 ? closure.reminderDays[i - 1] : 0;
                if (day <= prev) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["observationClosure", "reminderDays", i],
                        message: "reminderDays must be strictly increasing",
                    });
                }
                if (closure.autoApproveDays !== undefined && day >= closure.autoApproveDays) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["observationClosure", "reminderDays", i],
                        message: `reminder day ${day} must be before autoApproveDays (${closure.autoApproveDays})`,
                    });
                }
            });
        }
    }
    if (recipe.observationDueDays !== undefined) {
        const severityKeys = new Set(recipe.severities.map((s) => s.key));
        for (const key of Object.keys(recipe.observationDueDays)) {
            if (!severityKeys.has(key)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["observationDueDays", key],
                    message: `observationDueDays key "${key}" is not a declared severity`,
                });
            }
        }
        for (const key of severityKeys) {
            if (!Object.prototype.hasOwnProperty.call(recipe.observationDueDays, key)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["observationDueDays"],
                    message: `observationDueDays must declare every severity — missing "${key}"`,
                });
            }
        }
    }
    // ADR-0137 — a recipe that reads the asset state from any report type
    // must be able to express "this is fine": without a positive severity
    // every derived item would read as a defect or as unreviewed.
    {
        const withSource = recipe.reportTypes.filter((t) => t.stateSource !== undefined);
        if (withSource.length > 0 && !recipe.severities.some((s) => s.conformance === "positive")) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["severities"],
                message: `reportType(s) ${withSource.map((t) => `"${t.key}"`).join(", ")} declare a stateSource, which requires at least one severity with conformance "positive"`,
            });
        }
    }
    // ADR-0169 — the photo review set S(item) is the item's
    // captured_photo_urls, which only the "checklist_items" state source
    // has; an auto-annul needs an observation lifecycle to move.
    recipe.reportTypes.forEach((t, i) => {
        if (!t.photoVerdictReview)
            return;
        if (t.stateSource !== "checklist_items") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["reportTypes", i, "photoVerdictReview"],
                message: `reportType "${t.key}" declares photoVerdictReview, which requires stateSource "checklist_items"`,
            });
        }
        if (t.photoVerdictReview.autoAnnulOnAllConforme && recipe.observationStatuses.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["reportTypes", i, "photoVerdictReview", "autoAnnulOnAllConforme"],
                message: `reportType "${t.key}" auto-annuls on all-conforme photos, which requires observationStatuses`,
            });
        }
    });
    // If brands are enabled at the recipe level, at least one membership
    // role must scope to "brand" — otherwise no one can administer the
    // brand layer the recipe just opted into. Without this check a
    // recipe could declare `brands.enabled: true` but only org-scoped
    // roles, which would be a configuration smell.
    if (recipe.brands?.enabled === true) {
        const hasBrandScope = recipe.memberRoles.some((r) => r.scopeType === "brand");
        if (!hasBrandScope) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["memberRoles"],
                message: 'recipe with brands.enabled=true must declare at least one memberRole with scopeType="brand"',
            });
        }
    }
    // ADR-0165 — a brand identity only means something when the recipe has
    // brands, and never on an org- or brand-scoped role: an org role spans
    // every brand, and a brand role's brand already IS its scope (two
    // sources for one fact would disagree sooner or later).
    recipe.memberRoles.forEach((r, i) => {
        if (r.brandIdentity === undefined)
            return;
        if (recipe.brands?.enabled !== true) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["memberRoles", i, "brandIdentity"],
                message: `memberRole "${r.key}" declares brandIdentity, which requires brands.enabled=true`,
            });
        }
        if (r.scopeType === "organization" || r.scopeType === "brand") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["memberRoles", i, "brandIdentity"],
                message: `memberRole "${r.key}" has scopeType="${r.scopeType}" and cannot declare brandIdentity`,
            });
        }
    });
});
/**
 * The brand identity the recipe declares for `role` (ADR-0165), or null
 * when it declares none — including a role the recipe does not know (a
 * legacy key on a live membership must read as "no identity", never
 * throw on the /organizations/me path).
 */
export function brandIdentityOf(recipe, role) {
    return recipe.memberRoles.find((r) => r.key === role)?.brandIdentity ?? null;
}
/**
 * The lanes that may give the conformity verdict (ADR-0134 §3), with
 * `observationClosure.closedBy` normalized to a list — a single lane
 * reads as a one-element list, an absent closure as none. THE one reader
 * of `closedBy`: every consumer compares by inclusion against this, so a
 * recipe can move from one lane to several without touching a caller.
 * Structural parameter so wire carriers (RecipeMeResponse) resolve too.
 */
export function closureLanes(recipe) {
    const closedBy = recipe.observationClosure?.closedBy;
    if (closedBy === undefined)
        return [];
    return Array.isArray(closedBy) ? [...new Set(closedBy)] : [closedBy];
}
/**
 * The severity keys a recipe explicitly declares as "no problem"
 * (ADR-0137). Rank 0 alone never implies positive — a severity counts
 * only when it carries `conformance: "positive"`. Structural parameter
 * so wire carriers (RecipeMeResponse) resolve too.
 */
export function positiveSeverityKeys(recipe) {
    return new Set(recipe.severities.filter((s) => s.conformance === "positive").map((s) => s.key));
}
export function getRecipe(registry, slug) {
    return registry.get(slug);
}
/**
 * The single mechanism behind the standalone-finding entry path (ADR-0111).
 * core-api's observations route gates report creation on it; the PWA picker
 * injects its fixed "Hallazgo independiente" option iff it returns non-null.
 * The superRefine above guarantees at most one such type per recipe.
 */
export function standaloneFindingReportType(recipe) {
    return (recipe.reportTypes.find((t) => (t.entryMode ?? "guided_flow") === "standalone_finding") ?? null);
}
/**
 * #0699 — whether a new report of `reportTypeKey` may be started from the
 * clients. Absent flag reads as creatable, so a recipe that never declares
 * `creatable` keeps every entry point it had. An unknown key reads as not
 * creatable: nothing to start.
 */
export function isReportTypeCreatable(recipe, reportTypeKey) {
    const rt = recipe.reportTypes.find((t) => t.key === reportTypeKey);
    return rt !== undefined && rt.creatable !== false;
}
/**
 * #0699 follow-up — whether `role` may start a report of `reportTypeKey`.
 * Same answer as isReportTypeCreatable() for every type that is creatable
 * for everyone; for a `creatable: false` type, true only when the role's
 * declared observation-status lane is listed in `creatableByActors`.
 * Reads `memberRoles[].observationStatusActor` directly instead of
 * resolveMemberRoleActor(): an unknown or lane-less role is simply "not
 * listed" here, never an error. Unknown type → false, like its sibling.
 */
export function isReportTypeCreatableForRole(recipe, reportTypeKey, role) {
    const rt = recipe.reportTypes.find((t) => t.key === reportTypeKey);
    if (rt === undefined)
        return false;
    if (rt.creatable !== false)
        return true;
    if (!role || !rt.creatableByActors)
        return false;
    const actor = recipe.memberRoles.find((r) => r.key === role)?.observationStatusActor;
    return actor !== undefined && rt.creatableByActors.includes(actor);
}
//# sourceMappingURL=recipe.js.map